

















































































import gql from 'graphql-tag';
import Vue, { PropType } from 'vue';
import { BCol, BRow } from 'bootstrap-vue';
import { ZenFormGroup } from 'zenComponents';
import ZenMultiselect from '@reciprocity/multiselect';
import { isEmpty, get } from 'lodash';
import { DropdownOption } from '@/components/cards/types';
import { MetricOptions, DimensionOptions, DIMENSIONS } from '@/constants/DashboardConstants';
import { DashboardBlock, DimensionType } from '../types';
import { createValueFromLabel, sortByLabel } from '@/utils/DashboardUtils';

const objectsToExclude = ['PERSON', 'EXTERNALREQUEST', 'FETCHER'];

export default Vue.extend({
  name: 'DashboardBlockSetup',
  components: {
    ZenFormGroup,
    ZenMultiselect,
    BCol,
    BRow,
  },
  props: {
    block: {
      type: Object as PropType<DashboardBlock>,
      default: () => {},
    },
    isEdit: {
      type: Boolean,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    selectedMetric: {
      type: Object as PropType<DropdownOption>,
      default: () => {},
    },
    selectedDimension: {
      type: Object as PropType<DropdownOption>,
      default: () => {},
    },
    selectedMappedObjectAttr: {
      type: Object as PropType<DropdownOption>,
      default: () => {},
    },
  },
  apollo: {
    customAttributes: {
      query: gql`
        query GetAttributesByMetric($metric: DashboardMetric!) {
          dashboardAttrs(metric: $metric) {
            customAttributes {
              value
              label
              type
            }
          }
        }
      `,
      skip() {
        return isEmpty(this.selectedMetric);
      },
      variables() {
        return {
          metric: this.selectedMetric.value,
        };
      },
      update(result) {
        return result.dashboardAttrs;
      },
    },
    mappedObjects: {
      query: gql`
        query GetMappedObjectsByMetric($metric: DashboardMetric!) {
          dashboardSeriesFilters(metric: $metric) {
            mappedObjects: filterByMappedObject {
              filterName
              filterType
            }
          }
        }
      `,
      skip() {
        return isEmpty(this.selectedMetric);
      },
      variables() {
        return {
          metric: this.selectedMetric.value,
        };
      },
      update(result) {
        const { mappedObjects } = result.dashboardSeriesFilters;
        let currentMappedObjects = mappedObjects.map(mappedObject => {
          return {
            value: createValueFromLabel(mappedObject.filterType),
            objectName: mappedObject.filterName,
            label: `map:${mappedObject.filterName}`,
            type: DimensionType.MAPPING,
          };
        });

        currentMappedObjects = currentMappedObjects.filter(function(el) {
          return !objectsToExclude.includes(el.value);
        });

        this.$emit('currentMappedObjectsChanged', currentMappedObjects);

        return currentMappedObjects;
      },
    },
    mappedObjectCustomAttributes: {
      query: gql`
        query GetMappedObjectAttributesByMetric($metric: DashboardMetric!) {
          dashboardAttrs(metric: $metric) {
            customAttributes {
              value
              label
              type
            }
          }
        }
      `,
      skip() {
        return isEmpty(this.selectedDimension) || this.selectedDimension.type !== DimensionType.MAPPING;
      },
      variables() {
        return {
          metric: this.selectedDimension.value,
        };
      },
      update(result) {
        return result.dashboardAttrs;
      },
    },
  },
  data() {
    return {
      mappedObjects: <any[]>[],
      mappedObjectCustomAttributes: <any[]>[],
      dimenisonType: DimensionType,
    };
  },
  computed: {
    metrics() {
      let currentMetrics = [];

      currentMetrics = Object.entries(MetricOptions)
        .map(([value, options]) => ({ value, label: options.label }))
        .sort(sortByLabel);

      this.preselectMetric(currentMetrics);
      return currentMetrics;
    },
    dimensionsForDisplay() {
      let attributes = [];
      let customAttributes = [];

      if (!isEmpty(this.selectedMetric)) {
        // get the correct dimensions
        attributes = get(MetricOptions, `${this.selectedMetric.value}.dimensions`, []).reduce(
          (attributes, attribute) => {
            // TODO: Remove this when counting by TAG is enabled
            if (attribute !== DIMENSIONS.TAG) {
              attributes[attribute] = DimensionOptions[attribute];
            }

            return attributes;
          },
          {}
        );
        attributes = Object.entries(attributes)
          .map(([value, options]) => ({ value, label: options.label, type: DimensionType.ATTRIBUTE }))
          .sort(sortByLabel);
      }
      if (!isEmpty(this.customAttributes)) {
        customAttributes = this.customAttributes.customAttributes.map(c => ({
          value: c.value,
          label: c.label,
          attrType: c.type,
          type: DimensionType.CUSTOM_ATTRIBUTE,
        }));
      }

      const dimensions = [
        {
          dimensionsGroup: 'Attributes',
          dimensions: attributes,
        },
        {
          dimensionsGroup: 'Custom Attributes',
          dimensions: customAttributes,
        },
        {
          dimensionsGroup: 'Mappings',
          dimensions: this.mappedObjects,
        },
      ];
      if (this.isEdit) {
        this.handleDimensionPreselection(dimensions);
      }
      return dimensions;
    },
    mappedObjectAttributes() {
      if (isEmpty(this.selectedDimension) || this.selectedDimension.type !== DimensionType.MAPPING) return [];

      let attributes = [];
      let customAttributes = [];

      attributes = get(MetricOptions, `${this.selectedDimension.value}.dimensions`, []).reduce(
        (attributes, attribute) => {
          // TODO: Remove this when counting by TAG is enabled
          if (attribute !== DIMENSIONS.TAG) {
            attributes[attribute] = DimensionOptions[attribute];
          }
          return attributes;
        },
        {}
      );
      attributes = Object.entries(attributes)
        .map(([value, options]) => ({ value, label: options.label }))
        .sort(sortByLabel);

      if (!isEmpty(this.mappedObjectCustomAttributes)) {
        customAttributes = this.mappedObjectCustomAttributes.customAttributes.map(c => ({
          value: c.value,
          label: c.label,
          attrType: c.type,
          type: DimensionType.CUSTOM_ATTRIBUTE,
        }));
      }

      const dimensions = [
        {
          dimensionsGroup: 'Attributes',
          dimensions: attributes,
        },
        {
          dimensionsGroup: 'Custom Attributes',
          dimensions: customAttributes,
        },
      ];
      return dimensions;
    },
  },
  watch: {
    mappedObjectAttributes(mappedObjectAttributes) {
      if (!isEmpty(mappedObjectAttributes)) {
        this.preselectExistingMappedObjectAttribute(mappedObjectAttributes);
      }
    },
  },
  methods: {
    preselectMetric(metrics: any[]) {
      if (this.isEdit) {
        this.$emit(
          'selectMetric',
          metrics.find(metric => metric.value === this.block.settings.metric)
        );
      } else {
        this.$emit('selectMetric', metrics[0]);
      }
    },
    handleDimensionPreselection(dimensions: any[]) {
      if (this.isEdit) {
        if (
          this.block.settings.customAttrId &&
          this.block.settings.mappedObjectAttr !== DimensionType.CUSTOM_ATTRIBUTE
        ) {
          this.preselectExistingCustomAttribute(dimensions);
        } else if (this.block.settings.mappedObject) {
          this.preselectExistingMappedObject(dimensions);
        } else if (this.block.settings.dimension) {
          this.preselectExistingAttribute(dimensions);
        } else {
          // In case nothing is selected - preselect with first
          this.$emit('selectDimension', dimensions[0].dimensions[0]);
        }
      }
    },
    preselectExistingAttribute(dimensions: any[]) {
      // check if there are any Attributes
      if (dimensions[0].dimensions.length) {
        // Select edited Attribute
        this.$emit(
          'selectDimension',
          dimensions[0].dimensions.find(dimension => dimension.value === this.block.settings.dimension)
        );
      }
    },
    preselectExistingCustomAttribute(dimensions: any[]) {
      // check if there are any Custom Attributes
      if (dimensions[1].dimensions.length) {
        // Select edited Attribute
        this.$emit(
          'selectDimension',
          dimensions[1].dimensions.find(dimension => dimension.value === this.block.settings.customAttrId)
        );
      }
    },
    preselectExistingMappedObject(dimensions: any[]) {
      // check if there are any Mappings
      if (dimensions[2].dimensions.length) {
        // Select edited Mapping
        this.$emit(
          'selectDimension',
          dimensions[2].dimensions.find(dimension => dimension.value === this.block.settings.mappedObject)
        );
      }
    },
    preselectExistingMappedObjectAttribute(mappedObjectAttributes: any[]) {
      // Select the correct attribute if not yet selected, is in edit and the block has mapped object attribute or select the first one
      if (isEmpty(this.selectedMappedObjectAttr) && this.isEdit) {
        if (this.block.settings.mappedObjectAttr !== DimensionType.CUSTOM_ATTRIBUTE) {
          if (mappedObjectAttributes[0].dimensions.length) {
            this.$emit(
              'selectMappedObjectAttr',
              mappedObjectAttributes[0].dimensions.find(
                attribute => attribute.value === this.block.settings.mappedObjectAttr
              )
            );
          }
        } else {
          if (mappedObjectAttributes[1].dimensions.length) {
            this.$emit(
              'selectMappedObjectAttr',
              mappedObjectAttributes[1].dimensions.find(
                attribute => attribute.value === this.block.settings.customAttrId
              )
            );
          }
        }
      }
    },
  },
});
